import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import TargetChip from "../TargetChip";
import { useNewScanWizard } from "../NewScanWizardProvider";

const SelectTargetsStep = () => {
  const navigate = useNavigate();
  const { targets, setTargets, authorizationConfirmed, setAuthorizationConfirmed } = useNewScanWizard();
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");

  const isValidIP = (str) => {
    const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (ipv4Regex.test(str)) {
      const parts = str.split(".");
      return parts.every(part => {
        const num = parseInt(part, 10);
        return num >= 0 && num <= 255;
      });
    }
    return false;
  };

  const isValidCIDR = (str) => {
    const cidrRegex = /^(\d{1,3}\.){3}\d{1,3}\/(\d{1,2})$/;
    if (!cidrRegex.test(str)) return false;
    
    const [ip, prefix] = str.split("/");
    const prefixNum = parseInt(prefix, 10);
    
    if (prefixNum < 0 || prefixNum > 32) return false;
    if (prefixNum > 24) {
      setError("Only CIDR blocks /24 or smaller are supported");
      return false;
    }
    
    return isValidIP(ip);
  };

  const isValidDomain = (str) => {
    const domainRegex = /^(?=.{1,253}$)([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,63}$/;
    return domainRegex.test(str);
  };

  const isValidURL = (str) => {
    const urlRegex = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;
    return urlRegex.test(str);
  };

  const isValidTarget = (target) => {
    target = target.trim();
    return isValidIP(target) || isValidCIDR(target) || isValidDomain(target) || isValidURL(target);
  };

  const handleAddTarget = (e) => {
    e.preventDefault();
    setError("");

    const input = inputValue.trim();
    if (!input) {
      setError("Enter a target");
      return;
    }

    if (!isValidTarget(input)) {
      setError("Invalid target format. Use IP, domain, CIDR (/24 or smaller), or HTTP(S) URL");
      return;
    }

    if (targets.includes(input)) {
      setError("Target already added");
      return;
    }

    setTargets([...targets, input]);
    setInputValue("");
  };

  const handleRemoveTarget = (target) => {
    setTargets(targets.filter(t => t !== target));
  };

  const handleNext = () => {
    if (targets.length === 0) {
      setError("Add at least one target");
      return;
    }

    if (!authorizationConfirmed) {
      setError("You must confirm authorization to scan these targets");
      return;
    }

    navigate("/scans/new/configure");
  };

  return (
    <div className="select-targets-step">
      <h2>Step 2: Select Targets</h2>
      <p className="step-description">
        Add targets to scan. Supported formats: IPv4 address, CIDR block (/24 or smaller), domain name, or HTTP(S) URL.
      </p>

      {error && <div className="error-banner">{error}</div>}

      <form onSubmit={handleAddTarget} className="target-input-form">
        <div className="input-group">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="e.g., example.com, 192.168.1.0/24, or https://example.com"
            className="target-input"
          />
          <button type="submit" className="add-button">
            Add Target
          </button>
        </div>
      </form>

      <div className="targets-display">
        <h3>Targets ({targets.length})</h3>
        {targets.length === 0 ? (
          <p className="empty-state">No targets added yet</p>
        ) : (
          <div className="targets-list">
            {targets.map(target => (
              <TargetChip
                key={target}
                target={target}
                onRemove={handleRemoveTarget}
              />
            ))}
          </div>
        )}
      </div>

      <div className="authorization-section">
        <label className="authorization-checkbox">
          <input
            type="checkbox"
            checked={authorizationConfirmed}
            onChange={(e) => setAuthorizationConfirmed(e.target.checked)}
          />
          <strong>
            I own these targets or have explicit written authorization to scan them.
          </strong>
        </label>
        <p className="authorization-note">
          Several selected tools can be genuinely disruptive to system availability and functionality. 
          By checking this box, you confirm that you have the right to test these targets and have obtained any 
          necessary approvals from their owners.
        </p>
      </div>

      <div className="step-actions">
        <button
          type="button"
          className="primary-button"
          onClick={handleNext}
          disabled={targets.length === 0 || !authorizationConfirmed}
        >
          Next →
        </button>
      </div>
    </div>
  );
};

export default SelectTargetsStep;
